import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

interface Patient {
  id: number
  patient_id: string
  full_name: string
  date_of_birth: string
  gender: string
}

interface ReportFormData {
  patient: string
  clinical_history: string
  gross_description: string
  microscopic_findings: string
  diagnosis: string
  conclusion: string
  comments: string
  selected_images: string[]
}

const ReportForm: React.FC = () => {
  const { id, slideId } = useParams<{ id?: string; slideId?: string }>()
  const navigate = useNavigate()
  const [patients, setPatients] = useState<Patient[]>([])
  const [patientSearchTerm, setPatientSearchTerm] = useState('')
  const [showPatientDropdown, setShowPatientDropdown] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [formData, setFormData] = useState<ReportFormData>({
    patient: '',
    clinical_history: '',
    gross_description: '',
    microscopic_findings: '',
    diagnosis: '',
    conclusion: '',
    comments: '',
    selected_images: []
  })

  const fetchPatients = async () => {
    try {
      const response = await fetch('/api/v1/pathology/patients/')
      if (response.ok) {
        const data = await response.json()
        setPatients(data.results || data)
      }
    } catch (error) {
      console.error('Error fetching patients:', error)
    }
  }

  const fetchReport = async () => {
    if (!id) return
    try {
      const response = await fetch(`/api/v1/pathology/reports/${id}/`)
      if (response.ok) {
        const data = await response.json()
        setFormData({
          patient: data.patient.toString(),
          clinical_history: data.clinical_history || '',
          gross_description: data.gross_description || '',
          microscopic_findings: data.microscopic_findings || '',
          diagnosis: data.diagnosis || '',
          conclusion: data.conclusion || '',
          comments: data.comments || '',
          selected_images: data.selected_images || []
        })
        // Set patient search term
        const patient = patients.find(p => p.id === data.patient)
        if (patient) {
          setPatientSearchTerm(`${patient.full_name} (${patient.patient_id})`)
        }
      }
    } catch (error) {
      console.error('Error fetching report:', error)
      toast.error('Failed to load report')
    }
  }

  useEffect(() => {
    fetchPatients()
  }, [])

  useEffect(() => {
    if (id && patients.length > 0) {
      fetchReport()
    }
  }, [id, patients])

  const filteredPatients = patients.filter(patient => {
    const search = patientSearchTerm.toLowerCase()
    return (
      patient.patient_id.toLowerCase().includes(search) ||
      patient.full_name.toLowerCase().includes(search)
    )
  })

  const handleSelectPatient = (patient: Patient) => {
    setFormData({ ...formData, patient: patient.id.toString() })
    setPatientSearchTerm(`${patient.full_name} (${patient.patient_id})`)
    setShowPatientDropdown(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.patient) {
      toast.error('Please select a patient')
      return
    }

    if (!formData.diagnosis) {
      toast.error('Diagnosis is required')
      return
    }

    setIsSubmitting(true)

    try {
      const url = id
        ? `/api/v1/pathology/reports/${id}/`
        : '/api/v1/pathology/reports/'

      const method = id ? 'PUT' : 'POST'

      const payload = {
        ...formData,
        patient: parseInt(formData.patient),
        slide_id: slideId || undefined
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        toast.success(id ? 'Report updated successfully' : 'Report created successfully')
        navigate('/pathology/reports')
      } else {
        const errorData = await response.json()
        toast.error(errorData.detail || 'Failed to save report')
      }
    } catch (error) {
      console.error('Error saving report:', error)
      toast.error('Error saving report')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Edit Report' : 'Create New Report'}
          </h1>
          <p className="text-gray-600">Fill in the report details below</p>
        </div>
        <button
          onClick={() => navigate('/pathology/reports')}
          className="text-gray-600 hover:text-gray-900"
        >
          ← Back to Reports
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg border p-6 space-y-6">
        {/* Patient Selection */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Patient <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Search by name or patient ID..."
            value={patientSearchTerm}
            onChange={(e) => {
              setPatientSearchTerm(e.target.value)
              setShowPatientDropdown(true)
            }}
            onFocus={() => setShowPatientDropdown(true)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            required
          />
          {showPatientDropdown && patientSearchTerm && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {filteredPatients.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No patients found
                </div>
              ) : (
                filteredPatients.map(patient => (
                  <div
                    key={patient.id}
                    onClick={() => handleSelectPatient(patient)}
                    className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm"
                  >
                    <div className="font-medium text-gray-900">{patient.full_name}</div>
                    <div className="text-xs text-gray-500">
                      ID: {patient.patient_id} • {patient.gender}, {new Date().getFullYear() - new Date(patient.date_of_birth).getFullYear()} years
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Clinical History */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Clinical History
          </label>
          <textarea
            value={formData.clinical_history}
            onChange={(e) => setFormData({ ...formData, clinical_history: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Patient's clinical history and symptoms..."
          />
        </div>

        {/* Gross Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gross Description
          </label>
          <textarea
            value={formData.gross_description}
            onChange={(e) => setFormData({ ...formData, gross_description: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Macroscopic examination findings..."
          />
        </div>

        {/* Microscopic Findings */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Microscopic Findings
          </label>
          <textarea
            value={formData.microscopic_findings}
            onChange={(e) => setFormData({ ...formData, microscopic_findings: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Microscopic examination findings..."
          />
        </div>

        {/* Diagnosis */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Diagnosis <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.diagnosis}
            onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Final diagnosis..."
            required
          />
        </div>

        {/* Conclusion */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Conclusion / Recommendations
          </label>
          <textarea
            value={formData.conclusion}
            onChange={(e) => setFormData({ ...formData, conclusion: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Summary and recommendations..."
          />
        </div>

        {/* Comments */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Comments
          </label>
          <textarea
            value={formData.comments}
            onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Any additional notes..."
          />
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/pathology/reports')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : (id ? 'Update Report' : 'Create Report')}
          </button>
        </div>
      </form>
    </div>
  )
}

export default ReportForm
